import { v4 as uuid } from 'uuid';

import { type JsonObject } from '../../../models/event';
import { CoreEventType } from '../core-events';

/**
 * Represents the Ping core event.
 *
 * A Ping is an event covering essential “lifecycle” moments.
 * The exact moment varies by platform. For example, we send a ping when:
 * - A new tab is created
 * and its implementation details follow the sessionStorage lifecycle.
 *
 * See full information here: https://developer.mozilla.org/en-US/docs/Web/API/Window/sessionStorage
 */
export type Ping = {
  /**
   * Checks if this is a new tab and sends a ping if it is.
   *
   * In web environments, this detects when a new tab is created by checking
   * if the session storage is empty, which happens when a new tab is created.
   */
  start: () => void;
};

/**
 * Creates a live Ping implementation for web
 */
export function createPing(track: (name: string, payload: JsonObject) => void): Ping {
  return {
    start: () => {
      if (typeof window === 'undefined') {
        throw new Error('Ping is not supported in this environment');
      }

      // Initialize this tab with a unique ID in session storage
      // If this is a new tab, session storage will be empty
      const isNewTab = window.sessionStorage.getItem('PICOX_PING') === null;

      // Set a unique ID for this tab
      if (isNewTab) {
        window.sessionStorage.setItem('PICOX_PING', uuid());

        // Create ping data for tab creation
        const pingPayload: PingPayload = {
          ping_type: 'tab_created',
        };

        // Call the callback with the ping data
        track(CoreEventType.ping, pingPayload);
      }
    },
  };
}

/**
 * The core-event Ping payload model
 */
export type PingPayload = {
  /**
   * Type of ping event
   */
  ping_type: (typeof PingType)[keyof typeof PingType];
};

/**
 * Types of ping events
 */
export const PingType = {
  /**
   * A new tab was created
   */
  TAB_CREATED: 'tab_created',
} as const;
